import { iso1A2Code } from '@rapideditor/country-coder';
import type {
  OnyxAddress,
  OnyxCoordinates,
} from '../components/address-input/interfaces';

export class AddressHelper {
  public static composeLabel(
    address: Omit<OnyxAddress, 'type' | 'label' | 'coordinates'>,
  ): string {
    const { street, houseNumber, apartmentNumber, zipCode, city, countryCode } =
      address;

    const streetLabel = street
      ? `${street}${houseNumber ? ` ${houseNumber}${apartmentNumber ? `/${apartmentNumber}` : ''}` : ''}`
      : '';
    const cityLabel = `${zipCode} ${city}`;
    const countryLabel = countryCode?.toUpperCase();

    return [streetLabel, cityLabel, countryLabel].filter(Boolean).join(', ');
  }

  public static composeCoordinatesLabel(
    address: Pick<OnyxAddress, 'coordinates'>,
  ): string {
    const coordinates = address.coordinates;
    return `${+coordinates.latitude.toFixed(5)}, ${+coordinates.longitude.toFixed(5)}`;
  }

  public static getCountryCode(
    coordinates: Pick<OnyxAddress, 'coordinates'> | OnyxCoordinates,
  ): string | null {
    coordinates =
      'coordinates' in coordinates ? coordinates.coordinates : coordinates;
    const { latitude, longitude } = coordinates;
    return iso1A2Code([longitude, latitude])?.toLowerCase() ?? null;
  }

  public static getHaversineDistance(
    from: Pick<OnyxAddress, 'coordinates'> | OnyxCoordinates,
    to: Pick<OnyxAddress, 'coordinates'> | OnyxCoordinates,
  ): number {
    from = 'coordinates' in from ? from.coordinates : from;
    to = 'coordinates' in to ? to.coordinates : to;

    const EARTH_RADIUS = 6.371e6;

    const toRadians = (degrees: number) => degrees * (Math.PI / 180);

    const deltaLatitude = toRadians(to.latitude - from.latitude);
    const deltaLongitude = toRadians(to.longitude - from.longitude);

    const a =
      Math.sin(deltaLatitude / 2) * Math.sin(deltaLatitude / 2) +
      Math.cos(toRadians(from.latitude)) *
        Math.cos(toRadians(to.latitude)) *
        Math.sin(deltaLongitude / 2) *
        Math.sin(deltaLongitude / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    return EARTH_RADIUS * c;
  }
}
